import { type DealQueueRow, isDealForfeitable } from '@carsfinders/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';

import { ConfirmDialog } from '../../../components/confirm-dialog';
import { t } from '../../../lib/i18n';
import { toast } from '../../../lib/toast';
import { addBidToLedger } from '../../bids/api';
import { useRelayAction, useRelayClaim } from '../../bids/hooks/use-bid-mutations';
import { cn, formatAmount, localVehicleHref } from '../../bids/utils';
import {
  DEALS_QUEUE_QUERY_KEY,
  advanceShipping,
  dealActionErrorCode,
  dealActionErrorDetail,
  forfeitDeposit,
  handOver,
  markInvoicePaid,
  remindInvoice,
} from '../api';
import { ForfeitDepositDialog } from './forfeit-deposit-dialog';

/** An inline, translated explanation under the row's buttons. */
export interface DealNotice {
  tone: 'info' | 'danger';
  text: string;
  /** The row is stale — the queue must be re-read. */
  refresh: boolean;
}

/**
 * A refused or failed action → the operator's sentence.
 *
 * `PAYMENTS_DISABLED` is a POLICY state, not a fault: invoices, mark-paid and
 * reminders answer 409 with it in prod until the owner flips
 * `PAYMENTS_ENABLED` (gate G6). It gets its own info-toned sentence, never the
 * generic failure — nothing is broken and retrying will not help. The
 * "someone moved it first" codes refresh the queue, because the row the
 * operator clicked no longer describes the deal.
 */
export function dealNoticeFor(error: unknown): DealNotice {
  const code = dealActionErrorCode(error);
  const info = (key: string, refresh = false): DealNotice => ({
    tone: 'info',
    text: t(`admin.deals.notice.${key}`),
    refresh,
  });
  switch (code) {
    case 'PAYMENTS_DISABLED':
      return info('paymentsDisabled');
    case 'STALE_SHIP_STEP':
      return info('staleShipStep', true);
    case 'NOT_SHIPPING':
      return info('notShipping', true);
    case 'NOT_DELIVERED':
      return info('notDelivered', true);
    case 'REMIND_TOO_SOON':
      return info('remindTooSoon');
    case 'INVOICE_ALREADY_PAID':
      return info('alreadyPaid', true);
    case 'INVOICE_CHANGED':
      return info('invoiceChanged', true);
    case 'ALREADY_CLAIMED':
      return info('alreadyClaimed', true);
    case 'NOT_CLAIMABLE':
      return info('notClaimable', true);
    case 'CLAIM_REQUIRED':
      return info('claimRequired');
    case 'INVALID_RELAY_TRANSITION':
      return info('relayMoved', true);
    case 'EXPOSURE_LIMIT_EXCEEDED':
      return { tone: 'danger', text: t('admin.deals.notice.exposureExceeded'), refresh: false };
    case 'INVALID_BODY':
      return { tone: 'danger', text: t('admin.deals.notice.invalidBody'), refresh: false };
    // «Утримати депозит» (owner decision a).
    case 'ALREADY_FORFEITED':
      return info('alreadyForfeited', true);
    case 'DEAL_NOT_FORFEITABLE':
      return info('notForfeitable', true);
    case 'PAYMENT_BEING_CHECKED':
      return info('paymentBeingChecked', true);
    case 'VALIDATION_ERROR':
      return { tone: 'danger', text: t('admin.deals.notice.reasonInvalid'), refresh: false };
    // «Додати до обліку» (local-ledger bridge): the row no longer describes an
    // admin's own win, or the ledger does not know the lot's auction yet.
    case 'NOT_WON':
      return info('notWon', true);
    case 'NOT_ADMIN_BID':
      return info('notAdminBid', true);
    case 'UNKNOWN_LOCAL_AUCTION':
      return {
        tone: 'info',
        text: t('admin.deals.notice.unknownLocalAuction', {
          auction: dealActionErrorDetail(error, 'auctionName') ?? '—',
        }),
        refresh: false,
      };
    default:
      return { tone: 'danger', text: t('admin.deals.notice.failed'), refresh: false };
  }
}

type Dialog = 'won' | 'lost' | 'markPaid' | 'handOver' | 'forfeit' | null;

const VARIANT_CLASS = {
  primary: 'border-cf-brand bg-cf-brand text-white hover:bg-cf-brand-hover',
  secondary: 'border-cf-line bg-cf-surface text-cf-ink hover:border-cf-ink',
} as const;

/** The shape every action shares — a button, or the «Відкрити в обліку» link. */
const ACTION_CLASS =
  'h-[34px] rounded-cf-md border px-3 text-xs font-semibold whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-brand disabled:opacity-50';

function ActionButton({
  label,
  variant,
  disabled,
  title,
  onClick,
}: {
  label: string;
  variant: keyof typeof VARIANT_CLASS;
  disabled: boolean;
  /** Why a disabled button is disabled — the hover hint. */
  title?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(ACTION_CLASS, VARIANT_CLASS[variant])}
    >
      {label}
    </button>
  );
}

export interface DealNextActionProps {
  row: DealQueueRow;
  /**
   * «Виставити рахунок» opens the finance drawer, which the PAGE owns (one
   * drawer, not one per row). `trigger` is the button, so the page can hand
   * focus back to it when the drawer closes.
   */
  onIssueInvoice: (row: DealQueueRow, trigger: HTMLElement) => void;
  /** The clock the outcome lock reads — injectable for tests. */
  now?: number;
}

/**
 * True while the lot's REAL auction is still running (owner, 2026-10-12): the
 * operator cannot know the outcome yet, and a premature «Не виграно» closed a
 * live, relayed bid an hour and a half before its auction ended. Same rule as
 * the bid page's `isAuctionStillOpen`: a lot with no known end is decidable.
 */
export function isOutcomeLocked(
  row: Pick<DealQueueRow, 'auctionEndAt'>,
  now: number = Date.now(),
): boolean {
  const end = row.auctionEndAt?.getTime();
  return end !== undefined && Number.isFinite(end) && end > now;
}

/**
 * THE next action of one «Угоди» row — `Redesign-D-Admin` artboard «Admin ·
 * Угоди», the `acts` of each deal. The server decides WHICH action
 * (`row.nextAction`, from `resolveDealStage`); this component only renders it
 * and runs it through the route that owns that write:
 *
 * - `mirror` — nothing (owner, 2026-10-25/26): placing the bid is the bid
 *   page's «Оброблено», and the row's stage column already reads «Не
 *   оброблено» / «Оброблено» — a pill here only repeated it.
 * - `record_outcome` «Виграно» / «Не виграно» — claim, then the relay outcome.
 * - `issue_invoice` «Виставити рахунок» — the finance drawer, prefilled.
 * - `confirm_payment` «Оплату отримано» (+ «Нагадати» unless the client already
 *   said «Я сплатив» — then the operator checks, nobody chases);
 *   `remind_payment` (overdue) offers the same two.
 * - `advance_shipping` «Наступний етап», `hand_over` «Видано клієнту».
 * - `add_to_ledger` «Додати до обліку» — an admin's own win becomes a card in
 *   the local ledger (`POST /admin/bids/:bidId/local-vehicle`); the queue then
 *   lists it under «В обліку» with `open_in_ledger` «Відкрити в обліку», a
 *   link to the card on local.cars-finders.com in a new tab.
 *
 * Every refusal becomes an inline sentence under the buttons, never a silent
 * no-op — `PAYMENTS_DISABLED` above all, which is what prod answers today.
 *
 * Beside the next action, a row that still waits for payment
 * (`isDealForfeitable`: `awaiting_payment` / `payment_overdue`) offers
 * «Утримати депозит» — owner decision (a): the client refused to pay, the whole
 * deposit is withheld. It opens {@link ForfeitDepositDialog}, which requires a
 * reason; on success the queue refetches and the row moves to «Завершені».
 */
export function DealNextAction({ row, onIssueInvoice, now }: DealNextActionProps) {
  const queryClient = useQueryClient();
  const relayMutation = useRelayAction();
  const claimMutation = useRelayClaim();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState<DealNotice | null>(null);
  /** A forfeit refusal shown INSIDE the dialog, so the typed reason is not lost. */
  const [forfeitNotice, setForfeitNotice] = useState<DealNotice | null>(null);
  const [reminded, setReminded] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const wasOpen = useRef(false);
  /** A dialog closed by a SUCCESSFUL write: the row is about to change, so focus the cell. */
  const succeeded = useRef(false);

  // «нагадано» describes this stage only; a row that moves on drops it.
  useEffect(() => {
    setReminded(false);
  }, [row.stage]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
  };

  const fail = (error: unknown) => {
    const next = dealNoticeFor(error);
    setNotice(next);
    if (next.refresh) refresh();
  };

  const dealMutation = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: refresh,
  });

  const busy = relayMutation.isPending || claimMutation.isPending || dealMutation.isPending;

  // Focus goes back to the button that opened a dialog on cancel or refusal.
  // After a successful write the queue refetches and that button is replaced by
  // the next stage's, so focus goes to the row's action cell — it stays
  // mounted (rows are keyed by bid) and does not strand focus on <body>.
  useEffect(() => {
    if (dialog !== null) {
      wasOpen.current = true;
      return;
    }
    if (!wasOpen.current) return;
    wasOpen.current = false;
    const target = returnFocus.current;
    if (!succeeded.current && target && target.isConnected) target.focus();
    else cellRef.current?.focus();
    succeeded.current = false;
  }, [dialog]);

  const open = (next: Exclude<Dialog, null>) => (event: MouseEvent<HTMLButtonElement>) => {
    returnFocus.current = event.currentTarget;
    succeeded.current = false;
    setNotice(null);
    setForfeitNotice(null);
    setDialog(next);
  };

  /**
   * The dialog stays OPEN on a refusal the operator can read and act on
   * (payments switched off, a partly paid invoice, a bad reason), with the
   * sentence inside it. A stale row (already forfeited, moved on, being
   * checked, invoice changed) closes it, refreshes the queue and says why on
   * the row.
   */
  const forfeit = async (reason: string) => {
    const dealId = row.dealId;
    if (busy || dealId === null) return;
    setForfeitNotice(null);
    try {
      let totalChf = 0;
      await dealMutation.mutateAsync(async () => {
        const result = await forfeitDeposit(dealId, reason);
        totalChf = result.totalChf;
        return result;
      });
      succeeded.current = true;
      toast.success(t('admin.deals.forfeit.done', { amount: formatAmount(totalChf) }));
      setDialog(null);
    } catch (error) {
      const next = dealNoticeFor(error);
      if (next.refresh) {
        setNotice(next);
        refresh();
        setDialog(null);
      } else {
        setForfeitNotice(next);
      }
    }
  };

  /** Relay verbs need the operator's claim; re-claiming one's own row is a no-op heartbeat. */
  const claim = () => claimMutation.mutateAsync({ id: row.bidId, action: 'claim' });

  const runDeal = async (
    run: () => Promise<unknown>,
    successKey: string | ((result: unknown) => string) = 'done',
  ) => {
    if (busy) return false;
    setNotice(null);
    try {
      const result = await dealMutation.mutateAsync(run);
      succeeded.current = true;
      const key = typeof successKey === 'function' ? successKey(result) : successKey;
      toast.success(t(`admin.deals.notice.${key}`));
      return true;
    } catch (error) {
      fail(error);
      return false;
    } finally {
      setDialog(null);
    }
  };

  const outcomeLocked = isOutcomeLocked(row, now);

  const outcome = async (result: 'won' | 'lost') => {
    if (busy || outcomeLocked) return;
    try {
      await claim();
      await relayMutation.mutateAsync({
        id: row.bidId,
        action: 'outcome',
        body: { outcome: result },
      });
      succeeded.current = true;
      toast.success(t('admin.deals.notice.done'));
    } catch (error) {
      fail(error);
    } finally {
      setDialog(null);
    }
  };

  const invoice = row.invoice;
  const missingInvoice = (): void =>
    setNotice({ tone: 'danger', text: t('admin.deals.notice.noInvoice'), refresh: false });

  const buttons: ReactNode[] = [];
  const primary = 'primary' as const;
  const secondary = 'secondary' as const;

  switch (row.nextAction) {
    case 'mirror':
      // Owner, 2026-10-26: nothing here — the stage column already reads
      // «Не оброблено» / «Оброблено»; the bid is placed from its bid page.
      break;
    case 'record_outcome':
      buttons.push(
        <ActionButton
          key="won"
          label={t('admin.deals.actions.won')}
          variant={primary}
          disabled={busy || outcomeLocked}
          title={outcomeLocked ? t('admin.deals.outcomeLocked') : undefined}
          onClick={open('won')}
        />,
        <ActionButton
          key="lost"
          label={t('admin.deals.actions.lost')}
          variant={secondary}
          disabled={busy || outcomeLocked}
          title={outcomeLocked ? t('admin.deals.outcomeLocked') : undefined}
          onClick={open('lost')}
        />,
      );
      break;
    case 'issue_invoice':
      buttons.push(
        <ActionButton
          key="invoice"
          label={t('admin.deals.actions.issueInvoice')}
          variant={primary}
          disabled={busy}
          onClick={(event) => {
            setNotice(null);
            onIssueInvoice(row, event.currentTarget);
          }}
        />,
      );
      break;
    case 'confirm_payment':
    case 'remind_payment': {
      const checking = invoice?.status === 'checking';
      buttons.push(
        <ActionButton
          key="paid"
          label={t('admin.deals.actions.markPaid')}
          variant={primary}
          disabled={busy}
          onClick={invoice ? open('markPaid') : missingInvoice}
        />,
      );
      if (!checking && !reminded) {
        buttons.push(
          <ActionButton
            key="remind"
            label={t('admin.deals.actions.remind')}
            variant={secondary}
            disabled={busy}
            onClick={() => {
              if (!invoice) {
                missingInvoice();
                return;
              }
              void runDeal(() => remindInvoice(invoice.no), 'reminded').then((ok) => {
                if (ok) setReminded(true);
              });
            }}
          />,
        );
      }
      break;
    }
    case 'advance_shipping':
      buttons.push(
        <ActionButton
          key="next"
          label={t('admin.deals.actions.nextStage')}
          variant={primary}
          disabled={busy || row.dealId === null}
          onClick={() => {
            const { dealId, shipStep } = row;
            if (dealId === null) return;
            void runDeal(() => advanceShipping(dealId, shipStep ?? 0));
          }}
        />,
      );
      break;
    case 'hand_over':
      buttons.push(
        <ActionButton
          key="handover"
          label={t('admin.deals.actions.handOver')}
          variant={primary}
          disabled={busy || row.dealId === null}
          onClick={open('handOver')}
        />,
      );
      break;
    case 'add_to_ledger':
      buttons.push(
        <ActionButton
          key="ledger"
          label={t('admin.deals.actions.addToLedger')}
          variant={primary}
          disabled={busy}
          onClick={() =>
            void runDeal(
              () => addBidToLedger(row.bidId),
              (result) =>
                (result as { existing?: unknown } | null)?.existing === true
                  ? 'alreadyInLedger'
                  : 'addedToLedger',
            )
          }
        />,
      );
      break;
    case 'open_in_ledger':
      if (row.localVehicleId) {
        buttons.push(
          <a
            key="open-ledger"
            href={localVehicleHref(row.localVehicleId)}
            target="_blank"
            rel="noreferrer"
            aria-label={t('admin.deals.actions.openInLedgerAria', { car: row.carTitle })}
            className={cn(ACTION_CLASS, VARIANT_CLASS.secondary, 'inline-flex items-center')}
          >
            {t('admin.deals.actions.openInLedger')}
          </a>,
        );
      }
      break;
    case null:
      break;
  }

  if (isDealForfeitable(row.stage) && row.dealId !== null) {
    buttons.push(
      <button
        key="forfeit"
        type="button"
        onClick={open('forfeit')}
        disabled={busy}
        aria-label={t('admin.deals.actions.forfeitAria', { car: row.carTitle })}
        className="h-[34px] rounded-cf-md border border-cf-danger bg-cf-surface px-3 text-xs font-semibold whitespace-nowrap text-cf-danger hover:bg-cf-danger-tint focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-danger disabled:opacity-50"
      >
        {t('admin.deals.actions.forfeit')}
      </button>,
    );
  }

  const doneStage =
    row.stage === 'handed_over' ||
    row.stage === 'lost' ||
    row.stage === 'forfeited' ||
    row.stage === 'voided';
  const note = reminded
    ? t('admin.deals.note.reminded')
    : doneStage
      ? t('admin.deals.note.closed')
      : null;
  const car = row.carTitle;

  return (
    <div
      ref={cellRef}
      tabIndex={-1}
      className="flex min-w-0 flex-col items-end gap-1.5 outline-none"
      data-testid="deal-next-action"
    >
      <div className="flex flex-wrap items-center justify-end gap-1.5">
        {buttons}
        {note && (
          <span className="font-mono text-[11px] whitespace-nowrap text-cf-ink-subtle">{note}</span>
        )}
      </div>

      {row.nextAction === 'record_outcome' && outcomeLocked && (
        <span data-testid="deal-outcome-locked" className="text-[11px] text-cf-ink-subtle">
          {t('admin.deals.outcomeLockedShort')}
        </span>
      )}

      {notice && (
        <p
          role={notice.tone === 'danger' ? 'alert' : 'status'}
          className={cn(
            'max-w-[320px] rounded-cf-md border px-2.5 py-1.5 text-left text-[12px] leading-[1.4]',
            notice.tone === 'info'
              ? 'border-cf-info bg-cf-info-tint text-cf-info-ink'
              : 'border-cf-danger bg-cf-danger-tint text-cf-danger',
          )}
        >
          {notice.text}
        </p>
      )}

      {(dialog === 'won' || dialog === 'lost') && (
        <ConfirmDialog
          open
          tone={dialog === 'lost' ? 'danger' : 'default'}
          title={t(
            dialog === 'won' ? 'admin.deals.confirm.wonTitle' : 'admin.deals.confirm.lostTitle',
          )}
          message={t(
            dialog === 'won' ? 'admin.deals.confirm.wonBody' : 'admin.deals.confirm.lostBody',
            {
              car,
            },
          )}
          confirmLabel={t(
            dialog === 'won' ? 'admin.deals.actions.won' : 'admin.deals.actions.lost',
          )}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void outcome(dialog)}
        />
      )}
      {dialog === 'markPaid' && invoice && (
        <ConfirmDialog
          open
          title={t('admin.deals.confirm.markPaidTitle')}
          message={t('admin.deals.confirm.markPaidBody', {
            no: invoice.no,
            amount: formatAmount(invoice.dueChf),
          })}
          confirmLabel={t('admin.deals.actions.markPaid')}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => void runDeal(() => markInvoicePaid(invoice.no))}
        />
      )}
      {dialog === 'forfeit' && row.dealId !== null && (
        <ForfeitDepositDialog
          client={row.clientName}
          car={car}
          invoiceNo={invoice?.no ?? null}
          paidChf={invoice?.paidChf ?? 0}
          busy={busy}
          notice={forfeitNotice}
          onCancel={() => setDialog(null)}
          onSubmit={(reason) => void forfeit(reason)}
        />
      )}
      {dialog === 'handOver' && row.dealId !== null && (
        <ConfirmDialog
          open
          title={t('admin.deals.confirm.handOverTitle')}
          message={t('admin.deals.confirm.handOverBody', { car })}
          confirmLabel={t('admin.deals.actions.handOver')}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={() => {
            const dealId = row.dealId;
            if (dealId !== null) void runDeal(() => handOver(dealId));
          }}
        />
      )}
    </div>
  );
}
